<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useHead } from '@unhead/vue'
import AuthBrandHeader from '@/components/auth/AuthBrandHeader.vue'
import CandidateFinished from '@/views/recruiting/CandidateFinished.vue'

// Имя задано явно по той же причине, что и на странице запуска: правило генератора имён
// (`getRouteName` в `vite.config.ts`) — не то место, от которого должен зависеть адрес, по
// которому сюда перенаправляет сервер.
definePage({
  name: 'external-review-launch',
  meta: {
    public: true,
    layout: 'blank',
  },
})

/**
 * Разбор одной попытки внешнего запуска.
 *
 * Страница открывается ТОЛЬКО по подписанному адресу, на который сервер перенаправил с короткой
 * ссылки `/r/{код}`. Ни кода, ни идентификатора запуска отсюда никуда не передаётся: право —
 * подпись в собственном адресе страницы, и данные читаются повторением этого же адреса методом
 * POST (`GET` отдаёт саму страницу, `POST` — её данные, как и на загрузочной странице запуска).
 *
 * Из приложения здесь нет ничего: пустой layout, никакой навигации, никаких переходов. Ссылку
 * пересылают коллеге, и она обязана оставаться разбором одной попытки, а не входом в приложение.
 * Cookie нашего домена не участвуют вовсе — отсюда `credentials: 'omit'`: в браузере может быть
 * живая сессия сотрудника, и запрос не должен идти от её имени.
 */

interface ReviewSkill {
  title: string
  score: number
  recommendation: string
}

interface ReviewComment {
  timestamp: number
  text: string
}

interface ReviewResponse {
  scenario_title: string
  attempt_no: number
  score: number | null
  passed: boolean | null
  pass_threshold: number
  skills: ReviewSkill[]
  comments: ReviewComment[]
}

/** Отказ, который человек видит вместо разбора. Свой текст у каждого, разница для него реальна. */
type FailureKind = 'not_found' | 'expired' | 'generic'

const FAILURE_STYLE: Record<FailureKind, { icon: string; color: string }> = {
  not_found: { icon: 'tabler-link-off', color: 'error' },
  expired: { icon: 'tabler-clock-off', color: 'warning' },
  generic: { icon: 'tabler-alert-triangle', color: 'error' },
}

const { t, locale, availableLocales } = useI18n()

const loading = ref(true)
const review = ref<ReviewResponse | null>(null)
const failure = ref<FailureKind | null>(null)

const failureView = computed(() => {
  const kind = failure.value ?? 'generic'

  return {
    ...FAILURE_STYLE[kind],
    title: t(`externalLaunch.review.error.${kind}.title`),
    text: t(`externalLaunch.review.error.${kind}.text`),
  }
})

/** Итог попытки одной строкой: сдал, не сдал либо «оценки ещё нет». */
const outcome = computed(() => {
  if (review.value?.passed === true)
    return { text: t('externalLaunch.review.result.passed'), color: 'success' }

  if (review.value?.passed === false)
    return { text: t('externalLaunch.review.result.failed'), color: 'error' }

  return { text: t('externalLaunch.review.result.pending'), color: 'secondary' }
})

/**
 * Язык страницы — ровно тот же способ, что и на загрузочной странице запуска: публичной ручки,
 * которая отдала бы локаль организации, на сегодня нет, а логина у читателя разбора не было.
 */
function applyReviewLocale(): void {
  const preferred = import.meta.env.VITE_APP_RU === 'only'
    ? 'ru'
    : String(useCookie<string | null>('language').value ?? '')

  if (preferred && availableLocales.includes(preferred))
    locale.value = preferred
}

applyReviewLocale()

useHead({ title: t('externalLaunch.review.document_title') })

/** Отметка времени комментария к записи: секунды от начала игры в виде `мм:сс`. */
function formatTimestamp(seconds: number): string {
  const total = Math.max(0, Math.trunc(seconds))
  const minutes = Math.trunc(total / 60)

  return `${minutes}:${String(total % 60).padStart(2, '0')}`
}

/**
 * Чтение разбора. Адрес — собственный адрес страницы вместе с query-строкой: в ней лежит подпись,
 * и другого права у запроса нет. Ни cookie, ни XSRF: CSRF с `external-review/*` снят на сервере,
 * а сессионных прав ручка не использует вовсе.
 */
async function loadReview(): Promise<void> {
  try {
    const response = await fetch(`${window.location.pathname}${window.location.search}`, {
      method: 'POST',
      credentials: 'omit',
      headers: { Accept: 'application/json' },
    })

    if (response.status === 403) {
      failure.value = 'expired'

      return
    }

    if (response.status === 404) {
      failure.value = 'not_found'

      return
    }

    if (!response.ok) {
      failure.value = 'generic'

      return
    }

    review.value = await response.json() as ReviewResponse
  }
  catch {
    failure.value = 'generic'
  }
  finally {
    loading.value = false
  }
}

onMounted(loadReview)
</script>

<template>
  <div class="review-page">
    <div class="review-page__brand">
      <AuthBrandHeader />
    </div>

    <VProgressCircular
      v-if="loading"
      indeterminate
      color="primary"
      size="48"
      :aria-label="t('externalLaunch.review.loading')"
    />

    <CandidateFinished
      v-else-if="!review"
      :title="failureView.title"
      :text="failureView.text"
      :icon="failureView.icon"
      :color="failureView.color"
    />

    <template v-else>
      <VCard class="review-card">
        <VCardText>
          <h1 class="text-h5 mb-1">
            {{ review.scenario_title }}
          </h1>

          <p class="text-body-2 mb-6">
            {{ t('externalLaunch.review.attempt', { number: review.attempt_no }) }}
          </p>

          <div class="review-page__outcome">
            <VChip
              :color="outcome.color"
              variant="tonal"
              size="large"
            >
              {{ outcome.text }}
            </VChip>

            <span
              v-if="review.score !== null"
              class="text-h4"
            >
              {{ t('externalLaunch.review.score_of_100', { score: review.score }) }}
            </span>
          </div>

          <p class="text-body-2 mt-2 mb-0">
            {{ t('externalLaunch.review.threshold', { threshold: review.pass_threshold }) }}
          </p>
        </VCardText>
      </VCard>

      <VCard class="review-card">
        <VCardText>
          <h2 class="text-h6 mb-4">
            {{ t('externalLaunch.review.skills_title') }}
          </h2>

          <p
            v-if="!review.skills.length"
            class="text-body-2 mb-0"
          >
            {{ t('externalLaunch.review.skills_empty') }}
          </p>

          <template v-else>
            <div
              v-for="(skill, index) in review.skills"
              :key="`${skill.title}-${index}`"
              class="review-skill"
            >
              <div class="review-skill__head">
                <span class="text-subtitle-1">{{ skill.title }}</span>
                <VChip
                  size="small"
                  variant="tonal"
                >
                  {{ skill.score }}
                </VChip>
              </div>

              <p
                v-if="skill.recommendation"
                class="text-body-2 mb-0"
              >
                {{ skill.recommendation }}
              </p>
            </div>
          </template>
        </VCardText>
      </VCard>

      <VCard class="review-card">
        <VCardText>
          <h2 class="text-h6 mb-4">
            {{ t('externalLaunch.review.comments_title') }}
          </h2>

          <p
            v-if="!review.comments.length"
            class="text-body-2 mb-0"
          >
            {{ t('externalLaunch.review.comments_empty') }}
          </p>

          <template v-else>
            <div
              v-for="(comment, index) in review.comments"
              :key="`${comment.timestamp}-${index}`"
              class="review-comment"
            >
              <span class="review-comment__time text-body-2">{{ formatTimestamp(comment.timestamp) }}</span>
              <p class="text-body-2 mb-0">
                {{ comment.text }}
              </p>
            </div>
          </template>
        </VCardText>
      </VCard>
    </template>
  </div>
</template>

<style scoped lang="scss">
.review-page {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 2rem 1rem;
  min-block-size: 100vh;
  align-items: center;
}

.review-page__brand {
  color: rgb(var(--v-global-theme-primary));
}

.review-card {
  inline-size: 100%;
  max-inline-size: 720px;
}

.review-page__outcome {
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
}

.review-skill {
  padding-block: 0.75rem;
  border-block-start: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));

  &:first-of-type {
    border-block-start: none;
    padding-block-start: 0;
  }
}

.review-skill__head {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  margin-block-end: 0.25rem;
}

.review-comment {
  display: flex;
  gap: 0.75rem;
  padding-block: 0.5rem;
}

.review-comment__time {
  flex: 0 0 auto;
  opacity: 0.7;
  font-variant-numeric: tabular-nums;
}
</style>
